import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, MeetingProgress } from '../services/api.service';

const MEETING_POLL_MS = 2500;
const LIST_POLL_MS = 12000;

function isPageVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

type MeetingAutoRefreshOptions = {
  meetingId?: string;
  /** Poll while true (e.g. status === 'processing') */
  enabled: boolean;
  sectionEditing?: boolean;
  onSync: () => void | Promise<void>;
  onCompleted?: () => void;
  pollIntervalMs?: number;
};

/**
 * Polls GET /meetings/:id/progress while a meeting is processing.
 * On transition processing → completed, refetches materials via onSync.
 * Pauses while the tab is hidden or the user is editing a section.
 */
export function useMeetingAutoRefresh({
  meetingId,
  enabled,
  sectionEditing = false,
  onSync,
  onCompleted,
  pollIntervalMs = MEETING_POLL_MS,
}: MeetingAutoRefreshOptions) {
  const [liveProgress, setLiveProgress] = useState<MeetingProgress | null>(null);
  const [isLiveSyncing, setIsLiveSyncing] = useState(false);
  const lastStatusRef = useRef<string | null>(null);
  const syncingRef = useRef(false);

  const runSync = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setIsLiveSyncing(true);
    try {
      await onSync();
      onCompleted?.();
    } finally {
      syncingRef.current = false;
      setIsLiveSyncing(false);
    }
  }, [onSync, onCompleted]);

  useEffect(() => {
    if (!meetingId || !enabled || sectionEditing) {
      setLiveProgress(null);
      setIsLiveSyncing(false);
      lastStatusRef.current = null;
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (cancelled) return;
      if (!isPageVisible()) {
        timer = setTimeout(tick, pollIntervalMs);
        return;
      }

      try {
        const data = await apiService.getMeetingProgress(meetingId);
        if (cancelled) return;

        setLiveProgress(data);
        const prev = lastStatusRef.current;
        lastStatusRef.current = data.status;

        if (data.status === 'processing') {
          setIsLiveSyncing(true);
        } else if (prev === 'processing' && data.status === 'completed') {
          await runSync();
        } else if (data.status === 'error') {
          setIsLiveSyncing(false);
        } else {
          setIsLiveSyncing(false);
        }
      } catch {
        // transient network errors — keep polling
      }

      if (!cancelled) {
        timer = setTimeout(tick, pollIntervalMs);
      }
    };

    timer = setTimeout(tick, pollIntervalMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [meetingId, enabled, sectionEditing, pollIntervalMs, runSync]);

  return { liveProgress, isLiveSyncing };
}

type ListAutoRefreshOptions = {
  enabled: boolean;
  onRefresh: () => void | Promise<void>;
  pollIntervalMs?: number;
};

/** Refreshes meetings list while any item is still processing. */
export function useMeetingsListAutoRefresh({
  enabled,
  onRefresh,
  pollIntervalMs = LIST_POLL_MS,
}: ListAutoRefreshOptions) {
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (cancelled || !isPageVisible()) {
        timer = setTimeout(tick, pollIntervalMs);
        return;
      }
      try {
        await onRefresh();
      } catch {
        // ignore
      }
      if (!cancelled) {
        timer = setTimeout(tick, pollIntervalMs);
      }
    };

    const onVisible = () => {
      if (isPageVisible()) void onRefresh();
    };

    document.addEventListener('visibilitychange', onVisible);
    timer = setTimeout(tick, pollIntervalMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, onRefresh, pollIntervalMs]);
}
